/**
 * Generates a unique item reference number (0-99999) from article code + color + size
 * Uses djb2 hash algorithm — same inputs always produce the same number.
 *
 * @param {string} codigo - Article code
 * @param {string} colorName - Color name
 * @param {string} tallaName - Size name
 * @returns {number} A number between 0 and 99999
 */
export function generateItemReference(codigo, colorName, tallaName) {
  const str = `${codigo}-${colorName}-${tallaName}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash) % 100000;
}

/**
 * EAN-13 Generator Utility for RFID/SGTIN-96 Compatibility
 *
 * Structure for Forever Tentación:
 * - Company Prefix: 7500010 (7 digits, fixed)
 * - Item Reference: 5 digits (unique per SKU)
 * - Check Digit: 1 digit (calculated using GS1 algorithm)
 *
 * Total: 13 digits (EAN-13 format)
 */

export const COMPANY_PREFIX = "7500010";
export const ITEM_REFERENCE_LENGTH = 5;

/**
 * Calculates the GS1 check digit for EAN-13
 *
 * @param {string} digits - First 12 digits of the EAN-13
 * @returns {number} The check digit (0-9)
 */
export function calculateCheckDigit(digits) {
    if (digits.length !== 12) {
        throw new Error(`Expected 12 digits, got ${digits.length}`);
    }

    let sum = 0;
    for (let i = 0; i < 12; i++) {
        const digit = parseInt(digits[i], 10);
        sum += digit * (i % 2 === 0 ? 1 : 3);
    }

    return (10 - (sum % 10)) % 10;
}

/**
 * Generates a valid EAN-13 code from an item reference number
 *
 * @param {number} itemReference - The item reference number (0-99999)
 * @returns {string} A valid 13-digit EAN-13 code
 */
export function generateEAN13(itemReference) {
    if (itemReference < 0 || itemReference > 99999) {
        throw new Error(`Item reference must be between 0 and 99999`);
    }

    const paddedItemRef = itemReference.toString().padStart(ITEM_REFERENCE_LENGTH, "0");
    const first12Digits = COMPANY_PREFIX + paddedItemRef;
    const checkDigit = calculateCheckDigit(first12Digits);

    return first12Digits + checkDigit;
}

/**
 * Validates an EAN-13 code
 *
 * @param {string} ean13 - The EAN-13 code to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function validateEAN13(ean13) {
    if (!ean13 || ean13.length !== 13) {
        return false;
    }

    if (!/^\d{13}$/.test(ean13)) {
        return false;
    }

    const first12 = ean13.substring(0, 12);
    const providedCheckDigit = parseInt(ean13[12], 10);
    const calculatedCheckDigit = calculateCheckDigit(first12);

    return providedCheckDigit === calculatedCheckDigit;
}

/**
 * Checks if a code is a valid EAN-13 (numeric, 13 digits, valid check digit)
 * Used to determine which barcode format to use
 *
 * @param {string} code - The code to check
 * @returns {boolean} True if valid EAN-13
 */
export function isEAN13(code) {
    return validateEAN13(code);
}

/**
 * Determines the best barcode format for a given code
 *
 * @param {string} code - The barcode value
 * @returns {string} 'EAN13' or 'CODE128'
 */
export function getBarcodeFormat(code) {
    if (isEAN13(code)) {
        return 'EAN13';
    }
    return 'CODE128';
}

/**
 * Partition table for SGTIN-96.
 * partition -> [companyPrefixBits, companyPrefixDigits]
 * The Company Prefix + Item Reference fields always total 44 bits.
 */
const SGTIN96_PARTITIONS = {
    0: [40, 12],
    1: [37, 11],
    2: [34, 10],
    3: [30, 9],
    4: [27, 8],
    5: [24, 7],
    6: [20, 6],
};

/**
 * Decodes a UHF RFID EPC in SGTIN-96 format (24 hex chars) into its EAN-13 / GTIN-13.
 *
 * An RFID reader returns the chip's EPC, not the printed EAN-13. SGTIN-96 packs the
 * GS1 Company Prefix + Item Reference (the GTIN) plus a unique serial into 96 bits.
 * This reverses that encoding so the scanned tag can be matched against stored EAN-13s.
 *
 * Example: "3035C9C3A83B000000000088" -> "7500010604166"
 *
 * @param {string} epc - The EPC as a hexadecimal string (spaces are ignored)
 * @returns {string|null} The 13-digit EAN-13 code, or null if not a valid SGTIN-96 EPC
 */
export function epcToEAN13(epc) {
    if (!epc) return null;

    const hex = epc.replace(/\s+/g, "").toUpperCase();

    // SGTIN-96 -> 96 bits -> 24 hex chars, header 0x30
    if (hex.length !== 24 || !/^[0-9A-F]{24}$/.test(hex)) return null;
    if (hex.substring(0, 2) !== "30") return null;

    // Hex -> 96-bit binary string
    let bits = "";
    for (let i = 0; i < hex.length; i++) {
        bits += parseInt(hex[i], 16).toString(2).padStart(4, "0");
    }

    // Layout: Header(8) Filter(3) Partition(3) CompanyPrefix(P) ItemReference(R) Serial(38)
    const partition = parseInt(bits.substring(11, 14), 2);
    const partInfo = SGTIN96_PARTITIONS[partition];
    if (!partInfo) return null;

    const [cpBits, cpDigits] = partInfo;
    const itemRefBits = 44 - cpBits;       // CompanyPrefix + ItemReference = 44 bits
    const itemRefDigits = 13 - cpDigits;   // includes the leading indicator digit

    const cpStart = 14;
    const itemRefStart = cpStart + cpBits;

    const companyPrefix = parseInt(bits.substring(cpStart, itemRefStart), 2)
        .toString()
        .padStart(cpDigits, "0");
    const itemRefField = parseInt(
        bits.substring(itemRefStart, itemRefStart + itemRefBits),
        2
    )
        .toString()
        .padStart(itemRefDigits, "0");

    // Drop the indicator digit (first) to build the GTIN-13 / EAN-13
    const itemReference = itemRefField.substring(1);
    const first12 = companyPrefix + itemReference;
    if (first12.length !== 12 || !/^\d{12}$/.test(first12)) return null;

    return first12 + calculateCheckDigit(first12);
}
